import WebSocketTransport from '@cubejs-client/ws-transport'

import type { RealtimeStatus } from '@/services/contracts'

/** Sözleşme §6: yeniden bağlanma 1 sn'den başlar, ikiye katlanır, 30 sn'de durur. */
const MIN_RETRY_MS = 1_000
const MAX_RETRY_MS = 30_000

/** `attempts`: el sıkışmaya varamadan biten ardışık deneme sayısı (≥ 1). */
export function retryDelay(attempts: number): number {
  return Math.min(MAX_RETRY_MS, MIN_RETRY_MS * 2 ** (attempts - 1))
}

export interface LiveTransportHandlers {
  onStatus: (status: RealtimeStatus) => void
  /** Sunucudan veri taşıyan bir mesaj geldi (Cube yalnızca sonuç değişince gönderir). */
  onData: () => void
}

/**
 * `WebSocketTransport`'a iki şey ekler (bkz. docs/CUBE-MIGRATION-PLAN.md K5):
 * - Yeniden bağlanmada üstel bekleme. Kütüphane soket kapanınca **beklemeden**
 *   yeniden bağlanıyor; Cube kapalıyken nginx'in 502'siyle sıcak döngüye giriyor.
 * - Bağlantı durumu callback'i (kütüphane hiç olay yayınlamıyor).
 *
 * Kütüphanenin korumalı `initSocket`/`sendMessage`/`ws` üyelerine dayanır; bu
 * yüzden `@cubejs-client/ws-transport` sürümü `package.json`'da sabittir.
 * Sürüm yükseltmesinde `cube-ws-transport.test.ts` bu varsayımı doğrular.
 */
export class LiveTransport extends WebSocketTransport {
  private failedAttempts = 0
  private retry: Promise<void> | null = null
  private closed = false
  private readonly handlers: LiveTransportHandlers

  constructor(apiUrl: string, handlers: LiveTransportHandlers) {
    super({ apiUrl })
    this.handlers = handlers
  }

  /** Açık (ya da açılmakta olan) bir soket var mı — token rotasyonu yalnızca o zaman gerekir. */
  get hasSocket(): boolean {
    return this.ws !== null
  }

  protected override initSocket(): Promise<void> {
    // Kapatılmış transport'a gelen geç çağrılar (ör. unmount'taki unsubscribe) yeni soket açmasın.
    if (this.closed) return new Promise<void>(() => {})
    if (this.ws) return super.initSocket() as Promise<void>
    if (this.failedAttempts === 0) return this.connect()
    this.retry ??= new Promise<void>((resolve) => {
      setTimeout(resolve, retryDelay(this.failedAttempts))
    }).then(() => {
      this.retry = null
      return this.connect()
    })
    return this.retry
  }

  protected override sendMessage(message: unknown): void {
    if (!this.closed) super.sendMessage(message)
  }

  override async close(): Promise<void> {
    this.closed = true
    this.messageIdToSubscription = {}
    this.messageQueue = []
    await super.close()
    this.handlers.onStatus('closed')
  }

  private connect(): Promise<void> {
    // El sıkışmada sıfırlanır; sıfırlanmadan kapanırsa sonraki deneme bekler.
    this.failedAttempts += 1
    this.handlers.onStatus(this.failedAttempts > 1 ? 'reconnecting' : 'connecting')
    const ready = super.initSocket() as Promise<void>
    const socket = this.ws as WebSocket
    socket.addEventListener('message', (event: MessageEvent) => {
      const message = JSON.parse(String(event.data)) as { handshake?: boolean; message?: unknown }
      if (message.handshake) {
        this.failedAttempts = 0
        this.handlers.onStatus('open')
      } else if (message.message !== undefined) {
        this.handlers.onData()
      }
    })
    socket.addEventListener('close', () => {
      if (!this.closed) this.handlers.onStatus('reconnecting')
    })
    return ready
  }
}
